export class VideoModalController {
	constructor(options = {}) {
		this.root = options.root || document;
		this.modalSelector = options.modalSelector || ".uk-modal";
		this.fallbackTimeoutMs = Number.isFinite(options.fallbackTimeoutMs) ? options.fallbackTimeoutMs : 12000;
		this.i18nDomain = options.i18nDomain || "datarobot";

		this.modals = [];
		this.bound = false;
		this._bindings = [];
	}

	attach() {
		if (this.bound) {
			return this.modals.length > 0;
		}
		if (!this.root || !(this.root instanceof Element || this.root === document)) {
			return false;
		}

		this.modals = Array.from(this.root.querySelectorAll(this.modalSelector));
		if (!this.modals.length) {
			return false;
		}

		this.modals.forEach((modal) => {
			if (this.hasYouTubeEmbed(modal)) {
				this.bindYouTubeModal(modal);
			} else {
				this.bindModal(modal);
			}
		});

		this.installGlobalGuardsOnce();
		this.bound = true;
		return true;
	}

	detach() {
		if (!this.bound) {
			return;
		}
		this._bindings.forEach((b) => {
			try {
				b.el.removeEventListener(b.type, b.handler);
			} catch (e) {}
		});
		this._bindings = [];
		this.modals.forEach((modal) => {
			this.clearFallbackTimer(modal);
			this.disconnectObserver(modal);
			this.setActiveModal(null);
		});
		this.bound = false;
	}

	getUnavailableText() {
		try {
			if (window.wp && window.wp.i18n && typeof window.wp.i18n.__ === "function") {
				return window.wp.i18n.__("Video unavailable", this.i18nDomain);
			}
		} catch (e) {}
		return "Video unavailable";
	}

	clearFallbackTimer(modalEl) {
		if (modalEl && modalEl._drWistiaFallbackTimer) {
			window.clearTimeout(modalEl._drWistiaFallbackTimer);
			modalEl._drWistiaFallbackTimer = null;
		}
	}

	disconnectObserver(modalEl) {
		if (modalEl && modalEl._drWistiaObserver) {
			try {
				modalEl._drWistiaObserver.disconnect();
			} catch (e) {}
			modalEl._drWistiaObserver = null;
		}
	}

	resetFallback(modalEl) {
		if (!modalEl) return;
		delete modalEl.dataset.wistiaUnavailable;
		const placeholder = modalEl.querySelector(".ssc-wistia-unavailable");
		if (placeholder && placeholder.parentNode) {
			placeholder.parentNode.removeChild(placeholder);
		}
		const baseEmbed = modalEl.querySelector('.wistia_embed[class*="wistia_async_"]');
		if (baseEmbed && baseEmbed.style) {
			baseEmbed.style.display = "";
		}
	}

	showFallback(modalEl) {
		if (!modalEl || modalEl.dataset.wistiaUnavailable === "1") {
			return;
		}
		modalEl.dataset.wistiaUnavailable = "1";
		this.clearFallbackTimer(modalEl);

		try {
			const iframes = modalEl.querySelectorAll("iframe, video");
			iframes.forEach((n) => n && n.parentNode && n.parentNode.removeChild(n));
		} catch (e) {}

		try {
			const scripts = modalEl.querySelectorAll("script[src*='wistia']");
			scripts.forEach((n) => n && n.parentNode && n.parentNode.removeChild(n));
		} catch (e) {}

		const baseEmbed = modalEl.querySelector('.wistia_embed[class*="wistia_async_"]');
		if (baseEmbed && baseEmbed.style) {
			baseEmbed.style.display = "none";
		}

		const dialog = modalEl.querySelector(".uk-modal-dialog") || modalEl;
		if (!dialog.querySelector(".ssc-wistia-unavailable")) {
			const placeholder = document.createElement("div");
			placeholder.className = "uk-padding uk-text-center ssc-wistia-unavailable";
			placeholder.textContent = this.getUnavailableText();
			dialog.appendChild(placeholder);
		}
	}

	hasYouTubeEmbed(modalEl) {
		return Boolean(modalEl.querySelector('iframe[src*="youtube.com"], iframe[src*="youtu.be"]'));
	}

	stopYouTube(modalEl) {
		const iframes = modalEl.querySelectorAll('iframe[src*="youtube.com"], iframe[src*="youtu.be"]');
		iframes.forEach((iframe) => {
			const src = iframe.getAttribute("src");
			if (src) {
				iframe.setAttribute("src", "");
				iframe.dataset.ytSrc = src;
			}
		});
	}

	restoreYouTube(modalEl) {
		const iframes = modalEl.querySelectorAll("iframe[data-yt-src]");
		iframes.forEach((iframe) => {
			if (iframe.dataset.ytSrc && !iframe.getAttribute("src")) {
				iframe.setAttribute("src", iframe.dataset.ytSrc);
			}
		});
	}

	isWistiaRelated(value) {
		try {
			const msg = String(value && value.message ? value.message : value);
			if (msg.indexOf("Failed to fetch") !== -1) return true;
			if (msg.indexOf("NetworkError") !== -1) return true;
			if (msg.indexOf("ERR_TIMED_OUT") !== -1) return true;
			if (msg.indexOf("ERR_HTTP2_PING_FAILED") !== -1) return true;
			if (msg.indexOf("StopGo#synchronize timed out") !== -1) return true;
			const stack = value && value.stack ? String(value.stack) : "";
			if (stack && stack.toLowerCase().indexOf("wistia") !== -1) return true;
		} catch (e) {}
		return false;
	}

	setActiveModal(modalEl) {
		try {
			window._drActiveWistiaModal = modalEl || null;
			window._drActiveWistiaUntil = modalEl ? Date.now() + 20000 : 0;
		} catch (e) {}
	}

	installGlobalGuardsOnce() {
		if (window._drWistiaGuardsInstalled) return;
		window._drWistiaGuardsInstalled = true;

		window.addEventListener(
			"unhandledrejection",
			(event) => {
				try {
					const modalEl = window._drActiveWistiaModal;
					const until = Number(window._drActiveWistiaUntil || 0);
					if (!modalEl || Date.now() > until) return;
					if (!this.isWistiaRelated(event && event.reason)) return;
					if (event && typeof event.preventDefault === "function") {
						event.preventDefault();
					}
					modalEl._drWistiaSawFailure = true;
				} catch (e) {}
			},
			true
		);

		window.addEventListener(
			"error",
			(event) => {
				try {
					const modalEl = window._drActiveWistiaModal;
					const until = Number(window._drActiveWistiaUntil || 0);
					if (!modalEl || Date.now() > until) return;
					const filename = event && event.filename ? String(event.filename) : "";
					const isWistiaFile = filename.toLowerCase().indexOf("wistia") !== -1;
					if (!isWistiaFile && !this.isWistiaRelated(event && event.error)) return;
					if (event && typeof event.preventDefault === "function") {
						event.preventDefault();
					}
					modalEl._drWistiaSawFailure = true;
				} catch (e) {}
			},
			true
		);
	}

	ensureWistiaApiLoaded() {
		window._wq = window._wq || [];
		if (typeof window.Wistia === "undefined") {
			const existingAny = document.querySelector('script[src*="wistia.com/assets/external/E-v1.js"]');
			if (!existingAny) {
				const s = document.createElement("script");
				s.src = "https://fast.wistia.com/assets/external/E-v1.js";
				s.async = true;
				try {
					document.body.appendChild(s);
				} catch (e) {}
			}
		}
	}

	getEmbedAndVideoId(container) {
		const divEmbed = container.querySelector('.wistia_embed[class*="wistia_async_"]');
		if (divEmbed) {
			let videoId = null;
			divEmbed.classList.forEach((cls) => {
				if (cls.indexOf("wistia_async_") === 0) {
					videoId = cls.substring("wistia_async_".length);
				}
			});
			return { embed: divEmbed, videoId };
		}
		const iframe = container.querySelector('iframe[src*="wistia."][src*="/embed/iframe/"]');
		if (iframe) {
			const m = iframe.src.match(/\/embed\/iframe\/([^?&#/]+)/);
			if (m && m[1]) {
				return { embed: iframe, videoId: m[1] };
			}
		}
		const jsonp = container.querySelector('script[src*="/embed/medias/"]');
		if (jsonp) {
			const m = jsonp.src.match(/\/embed\/medias\/([^.?&#/]+)/);
			if (m && m[1]) {
				return { embed: null, videoId: m[1] };
			}
		}
		return { embed: null, videoId: null };
	}

	tryPlay(modalEl) {
		const { embed, videoId } = this.getEmbedAndVideoId(modalEl);
		this.ensureWistiaApiLoaded();

		if (embed && embed.id && window.Wistia && typeof window.Wistia.api === "function") {
			const player = window.Wistia.api(embed.id);
			if (player) {
				modalEl._wistiaPlayer = player;
				try {
					player.time(0).play();
				} catch (e) {}
			}
		}

		if (videoId) {
			window._wq.push({
				id: videoId,
				onReady: function (video) {
					modalEl._wistiaPlayer = video;
					try {
						video.time(0).play();
					} catch (e) {}
				},
			});
		}
	}

	stopAndReset(modalEl) {
		const player = modalEl._wistiaPlayer;
		if (player) {
			try {
				player.pause();
				player.time(0);
			} catch (e) {}
		}
		const { embed, videoId } = this.getEmbedAndVideoId(modalEl);
		if (embed && embed.id && window.Wistia && typeof window.Wistia.api === "function") {
			const p = window.Wistia.api(embed.id);
			if (p) {
				try {
					p.pause();
					p.time(0);
				} catch (e) {}
			}
		}
		if (!embed && videoId && window.Wistia && typeof window.Wistia.api === "function") {
			const p2 = window.Wistia.api(videoId);
			if (p2) {
				try {
					p2.pause();
					p2.time(0);
				} catch (e) {}
			}
		}
	}

	destroyPlayer(modalEl) {
		const player = modalEl._wistiaPlayer;
		if (!player) {
			return;
		}
		try {
			if (typeof player.pause === "function") {
				player.pause();
			}
			if (typeof player.time === "function") {
				player.time(0);
			}
			if (typeof player.unbind === "function") {
				player.unbind();
			}
		} catch (e) {}
		modalEl._wistiaPlayer = null;
	}

	armObserver(modalEl) {
		if (modalEl._wistiaObserver) return;
		const observer = new MutationObserver(() => {
			const ready = modalEl.querySelector(".wistia_embed[id]");
			if (ready && window.Wistia && typeof window.Wistia.api === "function") {
				const p = window.Wistia.api(ready.id);
				if (p) {
					modalEl._wistiaPlayer = p;
					try {
						p.time(0).play();
					} catch (e) {}
					observer.disconnect();
					modalEl._wistiaObserver = null;
				}
			}
		});
		modalEl._wistiaObserver = observer;
		observer.observe(modalEl, { childList: true, subtree: true, attributes: true, attributeFilter: ["id", "class"] });
	}

	bindModal(modal) {
		const onBeforeShow = () => {
			this.resetFallback(modal);
			this.clearFallbackTimer(modal);
			modal._drWistiaSawFailure = false;
			this.setActiveModal(modal);
			this.ensureWistiaApiLoaded();
		};
		const onShow = () => {
			this.setActiveModal(modal);
			this.ensureWistiaApiLoaded();
			this.armObserver(modal);
			const swatch = modal.querySelector(".wistia_swatch");
			if (swatch) swatch.style.display = "none";
		};
		const onShown = () => {
			this.setActiveModal(modal);
			this.ensureWistiaApiLoaded();
			this.armObserver(modal);
			this.tryPlay(modal);

			this.clearFallbackTimer(modal);
			modal._drWistiaFallbackTimer = window.setTimeout(() => {
				modal._drWistiaFallbackTimer = null;
				if (modal._wistiaPlayer) return;
				const hasIframe = Boolean(modal.querySelector("iframe[src*='wistia']"));
				const hasVideo = Boolean(modal.querySelector("video"));
				const hasEmbedId = Boolean(modal.querySelector(".wistia_embed[id]"));
				if (hasIframe || hasVideo || hasEmbedId) return;
				this.showFallback(modal);
			}, this.fallbackTimeoutMs);
		};
		const onBeforeHide = () => {
			this.stopAndReset(modal);
			this.destroyPlayer(modal);
			this.clearFallbackTimer(modal);
		};
		const onHide = () => {
			this.stopAndReset(modal);
			this.destroyPlayer(modal);
			this.clearFallbackTimer(modal);
			this.setActiveModal(null);
			this.disconnectObserver(modal);
		};
		const onHidden = () => {
			this.stopAndReset(modal);
			this.destroyPlayer(modal);
			this.clearFallbackTimer(modal);
			this.setActiveModal(null);
			this.disconnectObserver(modal);
		};

		modal.addEventListener("beforeshow", onBeforeShow);
		modal.addEventListener("show", onShow);
		modal.addEventListener("shown", onShown);
		modal.addEventListener("beforehide", onBeforeHide);
		modal.addEventListener("hide", onHide);
		modal.addEventListener("hidden", onHidden);

		this._bindings.push({ el: modal, type: "beforeshow", handler: onBeforeShow });
		this._bindings.push({ el: modal, type: "show", handler: onShow });
		this._bindings.push({ el: modal, type: "shown", handler: onShown });
		this._bindings.push({ el: modal, type: "beforehide", handler: onBeforeHide });
		this._bindings.push({ el: modal, type: "hide", handler: onHide });
		this._bindings.push({ el: modal, type: "hidden", handler: onHidden });
	}

	bindYouTubeModal(modal) {
		const onBeforeShow = () => this.restoreYouTube(modal);
		const onBeforeHide = () => this.stopYouTube(modal);

		modal.addEventListener("beforeshow", onBeforeShow);
		modal.addEventListener("beforehide", onBeforeHide);

		this._bindings.push({ el: modal, type: "beforeshow", handler: onBeforeShow });
		this._bindings.push({ el: modal, type: "beforehide", handler: onBeforeHide });
	}
}
